import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { RefreshCw, TrendingUp, TrendingDown, Minus, ChevronDown, MapPin, Search, BarChart3, Megaphone, Store, Sparkles, AlertTriangle, Target, ArrowRight, Radar, Trash2, Lock, Unlock, Zap } from 'lucide-react';
import { Card, Fade, Pager, daysAgoLabel, LiveDot, RangeNotice } from './os2ui';
import { authFetch } from '../../lib/auth';
import { useT } from '../../i18n';
import { KitxensEffectCard, EstimateDisclaimer } from '../../components/KitxensEffect';
import { TEAM_AVATARS } from '../../components/teamAvatars';
import BusinessPulse from './BusinessPulse';
import RachelExecutiveBrief from './RachelExecutiveBrief';

function AdminKitxensEffect({ isEs }) {
  const [period, setPeriod] = useState('year');
  const [d, setD] = useState(null);
  useEffect(() => {
    authFetch(`/os2/kitxens-effect?period=${period}`).then((r) => (r.ok ? r.json() : null)).then(setD).catch(() => {});
  }, [period]);
  if (!d?.has_data) return null;
  return (
    <div className="mt-6">
      <KitxensEffectCard d={d} period={period} setPeriod={setPeriod} isEs={isEs} testid="admin-kitxens-effect"
        title={isEs ? 'El efecto Kitxens · activo de ventas' : 'The Kitxens effect · sales asset'}
        subtitle={isEs ? `Caso vivo: ${d.restaurant} (instancia demo) — el mismo panel que ven los prospectos.` : `Live case: ${d.restaurant} (demo instance) — the same panel prospects see.`} />
    </div>
  );
}

/* Google Growth Intelligence Center — real data when connected, labeled sample data in Demo Mode. */

const fmt = (n) => (Number(n) || 0).toLocaleString();
const ago = (iso, isEs) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return isEs ? `hace ${m} min` : `${m} min ago`;
  const h = Math.round(m / 60);
  return isEs ? `hace ${h} h` : `${h}h ago`;
};

function DeltaBadge({ v }) {
  if (v == null) return <span className="text-[10.5px] text-gray-400">—</span>;
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-bold ${up ? 'text-emerald-600' : 'text-red-500'}`}>
      {up ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{up ? '+' : ''}{v}%
    </span>
  );
}

function Kpi({ label, value, delta, suffix = '', spark }) {
  const tone = delta == null ? '#94a3b8' : delta >= 0 ? '#10b981' : '#ef4444';
  let path = null;
  if (spark?.length > 1) {
    const max = Math.max(...spark, 1);
    const min = Math.min(...spark, 0);
    const rng = max - min || 1;
    path = spark.map((v, i) => `${i === 0 ? 'M' : 'L'}${(i / (spark.length - 1)) * 100},${28 - ((v - min) / rng) * 24}`).join(' ');
  }
  return (
    <Card className="p-4">
      <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-gray-400">{label}</p>
      <div className="mt-1 flex items-end justify-between gap-2">
        <p className="text-[24px] font-black tabular-nums leading-none text-gray-900">{value}{suffix}</p>
        <DeltaBadge v={delta} />
      </div>
      {path && (
        <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="mt-2 w-full h-7" aria-hidden data-testid="kpi-sparkline">
          <path d={`${path} L100,30 L0,30 Z`} fill={tone} opacity="0.12" />
          <path d={path} fill="none" stroke={tone} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>
      )}
    </Card>
  );
}

function LineChart({ data, k1, k2, h = 120, color1 = '#F77F11', color2 = '#94a3b8', l1, l2, isEs }) {
  const [hi, setHi] = useState(null);
  if (!data?.length) return null;
  const w = 640;
  const max = Math.max(...data.map((d) => Math.max(d[k1] || 0, k2 ? d[k2] || 0 : 0)), 1);
  const X = (i) => (i / (data.length - 1)) * w;
  const Y = (v) => h - ((v || 0) / max) * (h - 12);
  const pts = (k) => data.map((d, i) => `${X(i)},${Y(d[k])}`).join(' ');
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const i = Math.round(((e.clientX - r.left) / r.width) * (data.length - 1));
    setHi(Math.max(0, Math.min(data.length - 1, i)));
  };
  const d0 = hi != null ? data[hi] : null;
  const key = d0 ? (d0.date || d0.d || d0.day || '') : '';
  const fmtD = key && /^\d{4}-\d{2}-\d{2}/.test(key)
    ? new Date(`${key.slice(0, 10)}T12:00:00`).toLocaleDateString(isEs ? 'es-ES' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' })
    : key;
  return (
    <div className="relative" onMouseLeave={() => setHi(null)}>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full cursor-crosshair" role="img" aria-label="trend chart" onMouseMove={onMove}>
        {k2 && <polyline points={pts(k2)} fill="none" stroke={color2} strokeWidth="1.5" opacity="0.7" />}
        <polyline points={pts(k1)} fill="none" stroke={color1} strokeWidth="2.5" strokeLinejoin="round" />
        {hi != null && (
          <g>
            <line x1={X(hi)} x2={X(hi)} y1="0" y2={h} stroke="#9ca3af" strokeDasharray="3,3" strokeWidth="1" />
            <circle cx={X(hi)} cy={Y(d0[k1])} r="4.5" fill={color1} stroke="#fff" strokeWidth="1.5" />
            {k2 && <circle cx={X(hi)} cy={Y(d0[k2])} r="3.5" fill={color2} stroke="#fff" strokeWidth="1.5" />}
          </g>
        )}
        {data.map((d, i) => d.note ? (
          <circle key={i} cx={X(i)} cy={Y(d[k1])} r="4" fill="#ef4444"><title>{d.note}</title></circle>
        ) : null)}
      </svg>
      {d0 && (
        <div className="pointer-events-none absolute top-0 z-20 rounded-lg bg-gray-900 text-white text-[11px] px-2.5 py-1.5 shadow-xl whitespace-nowrap"
          style={{ left: `${(hi / (data.length - 1)) * 100}%`, transform: `translateX(${hi > data.length / 2 ? 'calc(-100% - 10px)' : '10px'})` }}
          data-testid="chart-tooltip">
          <p className="font-bold capitalize">{fmtD}</p>
          <p><span style={{ color: color1 }}>●</span> {l1 || k1}: <b className="tabular-nums">{(d0[k1] || 0).toLocaleString()}</b></p>
          {k2 && <p><span style={{ color: color2 }}>●</span> {l2 || k2}: <b className="tabular-nums">{(d0[k2] || 0).toLocaleString()}</b></p>}
        </div>
      )}
    </div>
  );
}

function ScoreCard({ s, isEs }) {
  const [open, setOpen] = useState(false);
  const tone = s.value >= 65 ? '#10b981' : s.value >= 40 ? '#F77F11' : '#ef4444';
  const circ = 2 * Math.PI * 26;
  const TrendIcon = s.trend === 'up' ? TrendingUp : s.trend === 'down' ? TrendingDown : Minus;
  return (
    <Card className="p-4" data-testid={`score-${s.key}`}>
      <div className="flex items-center gap-3">
        <svg width="64" height="64" viewBox="0 0 64 64" className="shrink-0">
          <circle cx="32" cy="32" r="26" fill="none" stroke="#e5e7eb" strokeWidth="6" />
          <circle cx="32" cy="32" r="26" fill="none" stroke={tone} strokeWidth="6" strokeLinecap="round"
            strokeDasharray={`${(s.value / 100) * circ} ${circ}`} transform="rotate(-90 32 32)" />
          <text x="32" y="37" textAnchor="middle" fontSize="16" fontWeight="800" fill="#111827">{s.value}</text>
        </svg>
        <div className="min-w-0">
          <p className="text-[13px] font-bold text-gray-900 leading-tight">{isEs ? s.name_es : s.name_en}</p>
          <p className="mt-0.5 flex items-center gap-1.5 text-[10.5px] text-gray-500">
            <TrendIcon className="w-3 h-3" style={{ color: tone }} />
            <span className={`rounded-full px-1.5 py-0.5 font-bold ${s.confidence === 'high' ? 'bg-emerald-50 text-emerald-700' : s.confidence === 'medium' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
              {isEs ? `confianza ${s.confidence === 'high' ? 'alta' : s.confidence === 'medium' ? 'media' : 'baja'}` : `${s.confidence} confidence`}
            </span>
            {s.provisional && <span className="text-gray-400">{isEs ? 'provisional' : 'provisional'}</span>}
          </p>
        </div>
      </div>
      <button onClick={() => setOpen(!open)} className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-semibold text-gray-400 hover:text-gray-600">
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
        {isEs ? '¿Cómo se calcula?' : 'How is it calculated?'}
      </button>
      {open && (
        <div className="mt-2 text-[11.5px] text-gray-600 space-y-1.5">
          <p>{isEs ? s.explanation_es : s.explanation_en}</p>
          {(s.drivers || []).map((d, i) => (
            <p key={i} className="flex items-center justify-between">
              <span>{isEs ? d.label_es : d.label_en}</span>
              <b className={d.impact >= 0 ? 'text-emerald-600' : 'text-red-500'}>{d.impact >= 0 ? '+' : ''}{d.impact}</b>
            </p>
          ))}
          <p className="pt-1 border-t border-gray-100 font-semibold text-[#d8651a]">→ {isEs ? s.action_es : s.action_en}</p>
        </div>
      )}
    </Card>
  );
}

const NARR = [
  { key: 'what_happened', icon: Sparkles, en: 'What happened?', es: '¿Qué pasó?', tone: 'text-emerald-600 bg-emerald-50' },
  { key: 'attention', icon: AlertTriangle, en: 'What needs attention?', es: '¿Qué requiere atención?', tone: 'text-amber-600 bg-amber-50' },
  { key: 'opportunity', icon: Target, en: 'What opportunity exists?', es: '¿Qué oportunidad existe?', tone: 'text-sky-600 bg-sky-50' },
  { key: 'next', icon: ArrowRight, en: 'What should I do next?', es: '¿Qué hago ahora?', tone: 'text-[#d8651a] bg-orange-50' },
];

function ScansPulse({ isEs, scans }) {
  const scrollToList = () => document.getElementById('live-scans')?.scrollIntoView({ behavior: 'smooth' });
  return (
    <button onClick={scrollToList} className="text-left w-full h-full" data-testid="growth-scans-pulse">
      <Card className="p-4 h-full border-2 border-[#F77F11]/40 bg-gradient-to-br from-orange-50/70 to-white hover:border-[#F77F11] transition-colors">
        <p className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-[#d8651a] bg-orange-100/80">
          <LiveDot color="orange" /> <Radar className="w-3 h-3" /> {isEs ? 'Scans Index AI · en vivo' : 'Index AI Scans · live'}
        </p>
        <p className="mt-2 text-[22px] font-black tabular-nums text-gray-900 leading-none">
          {scans ? scans.week : '…'} <span className="text-[11px] font-bold text-gray-400">{isEs ? 'esta semana' : 'this week'}</span>
        </p>
        <p className="mt-1.5 text-[11.5px] text-gray-600 leading-snug">
          {scans?.today > 0 && <b className="text-emerald-600">{scans.today} {isEs ? 'hoy' : 'today'} · </b>}
          {scans?.latest
            ? <>{isEs ? 'Último' : 'Latest'}: <b>{scans.latest.domain}</b> ({scans.latest.score}/100)</>
            : (isEs ? 'Sin scans todavía' : 'No scans yet')}
        </p>
        <p className="mt-1.5 text-[11px] font-bold text-[#d8651a] inline-flex items-center gap-1">
          {isEs ? 'Ver listado completo' : 'View full list'} <ArrowRight className="w-3 h-3" />
        </p>
      </Card>
    </button>
  );
}

function buildAiRecs(d, scans, isEs) {
  const recs = [];
  if (scans) {
    if (scans.week === 0) {
      recs.push({ es: 'Cero scans esta semana — comparte el Free Audit en redes y con tus partners para reactivar el pipeline.', en: 'Zero scans this week — share the Free Audit on socials and with partners to reactivate the pipeline.', to: '/admin/ai-os/affiliates' });
    } else {
      recs.push({ es: `${scans.week} scans esta semana — revisa quién no dejó email y pide a los partners que hagan seguimiento.`, en: `${scans.week} scans this week — check who left no email and ask partners to follow up.`, to: '/admin/ai-os/growth#scans' });
    }
  }
  if (d?.gsc?.opportunities?.length) {
    recs.push({ es: `${d.gsc.opportunities.length} keywords en posición 4–10 — un empujón SEO las lleva a página 1.`, en: `${d.gsc.opportunities.length} keywords ranking 4–10 — one SEO push gets them to page 1.`, to: '/admin/ai-os/growth' });
  }
  if (d?.ads && d.ads.status !== 'active') {
    recs.push({ es: 'Google Ads sin campañas activas — activa una campaña de marca con presupuesto mínimo.', en: 'Google Ads has no active campaigns — launch a low-budget brand campaign.', to: '/admin/ai-os/growth' });
  }
  if (recs.length < 3) {
    recs.push({ es: 'Publica el caso Casa Mia (+$288k) como artículo SEO — es tu mejor prueba social.', en: 'Publish the Casa Mia case (+$288k) as an SEO article — your strongest social proof.', to: '/admin/ai-os/marketing' });
  }
  return recs.slice(0, 3).map((r) => ({ text: isEs ? r.es : r.en, to: r.to }));
}

function AiTeamCard({ d, scans, isEs }) {
  const recs = buildAiRecs(d, scans, isEs);
  const AGENT_CYCLE = ['eva', 'penny', 'stan', 'sonny'];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (recs.length < 2) return undefined;
    const t = setInterval(() => setI((v) => v + 1), 7000);
    return () => clearInterval(t);
  }, [recs.length]);
  if (!recs.length) return null;
  const idx = i % recs.length;
  const r = recs[idx];
  const av = TEAM_AVATARS.find((a) => a.key === AGENT_CYCLE[idx % AGENT_CYCLE.length]);
  return (
    <div className="rounded-2xl p-4 h-full flex flex-col" style={{ background: '#17181A', border: '1px solid rgba(255,255,255,0.08)' }} data-testid="growth-aiteam-card">
      <div className="flex items-center gap-2.5">
        <img key={av?.key} src={av?.img} alt={av?.name} className="w-9 h-9 rounded-full object-cover border border-white/25" />
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#F77F11] flex items-center gap-1.5">
            <LiveDot color="orange" /> {av?.name} · AI Team
          </p>
          <p className="text-[10px] text-white/40 truncate">{av?.role}</p>
        </div>
      </div>
      <Link to={r.to} key={idx} className="mt-2.5 flex-1 text-[12px] leading-relaxed text-white/90 hover:text-white transition-colors" data-testid="growth-aiteam-msg">
        {r.text}
      </Link>
      <div className="mt-2.5 flex items-center justify-between gap-2">
        <p className="text-[9.5px] text-white/35">{isEs ? 'Aprendido de tus datos reales de la plataforma' : 'Learned from your real platform data'}</p>
        <span className="flex items-center gap-1">
          {recs.map((_, di) => (
            <button key={di} onClick={() => setI(di)} className={`h-1 rounded-full transition-all ${di === idx ? 'w-4 bg-[#F77F11]' : 'w-1 bg-white/25'}`} aria-label={`tip ${di + 1}`} />
          ))}
        </span>
      </div>
    </div>
  );
}


function AgentBridgeSection({ isEs }) {
  const [d, setD] = useState(null);
  const [page, setPage] = useState(0);
  useEffect(() => {
    authFetch('/os2/agent-bridge').then((r) => r.json()).then(setD).catch(() => setD({ items: [], total: 0, captured: 0, needs_review: 0 }));
  }, []);
  const PER = 10;
  const items = d?.items || [];
  const totalPages = Math.max(1, Math.ceil(items.length / PER));
  const pg = Math.min(page, totalPages - 1);
  const rows = items.slice(pg * PER, pg * PER + PER);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(isEs ? 'es' : 'en', { day: '2-digit', month: 'short' }) : '');
  const STATUS = {
    captured: ['bg-emerald-50 text-emerald-600', isEs ? '✓ En CRM' : '✓ In CRM'],
    needs_review: ['bg-amber-50 text-amber-600', isEs ? '🚧 Revisar' : '🚧 Review'],
    crm_error: ['bg-red-50 text-red-500', isEs ? '✕ Error CRM' : '✕ CRM error'],
  };
  return (
    <div id="agent-bridge" data-testid="growth-agent-bridge">
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
          <LiveDot /> 🤝 {isEs ? 'Agent Bridge · leads del equipo IA' : 'Agent Bridge · AI team leads'}
          {d && <span className="normal-case tracking-normal text-gray-300">· {d.total} total · {d.captured} {isEs ? 'en CRM' : 'in CRM'} · {d.needs_review} {isEs ? 'por revisar' : 'to review'}</span>}
        </p>
        {items.length > PER && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 tabular-nums">
            <button onClick={() => setPage(Math.max(0, pg - 1))} disabled={pg === 0} data-testid="bridge-prev" className="h-6 px-2.5 rounded-full border border-gray-200 bg-white font-semibold text-gray-600 disabled:opacity-35">‹</button>
            {pg * PER + 1}–{Math.min((pg + 1) * PER, items.length)} / {items.length}
            <button onClick={() => setPage(Math.min(totalPages - 1, pg + 1))} disabled={pg >= totalPages - 1} data-testid="bridge-next" className="h-6 px-2.5 rounded-full border border-gray-200 bg-white font-semibold text-gray-600 disabled:opacity-35">›</button>
          </span>
        )}
      </div>
      <Card className="p-2">
        {!d ? <p className="p-4 text-[12.5px] text-gray-400">…</p> : !rows.length ? (
          <p className="p-4 text-[12.5px] text-gray-400">{isEs ? 'Aún no ha llegado nada del equipo — cuando Stan, Eva o cualquier agente envíe un lead al puente (info@kitxens.com o API), aparecerá aquí con su estado.' : 'Nothing from the team yet — when Stan, Eva or any agent sends a lead through the bridge (info@kitxens.com or API), it will show here with its status.'}</p>
        ) : rows.map((b) => {
          const [cls, label] = STATUS[b.status] || STATUS.needs_review;
          return (
            <div key={b.id} className="px-3 py-2.5 border-b border-gray-50 last:border-0 hover:bg-orange-50/40 rounded-xl transition-colors" data-testid={`bridge-item-${b.id}`}>
              <div className="flex items-center gap-3">
                <span className="w-8 h-8 shrink-0 rounded-full bg-[#FFF7ED] border border-orange-200 text-[13px] flex items-center justify-center">🤝</span>
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-semibold text-gray-900 truncate">
                    {b.parsed?.name || b.email || (isEs ? 'Sin contacto identificado' : 'No contact identified')}
                    {b.parsed?.restaurant && <span className="text-gray-600 font-normal"> · {b.parsed.restaurant}</span>}
                    <span className="ml-1.5 text-[10px] font-semibold text-gray-400 tabular-nums">· {fmt(b.created_at)}</span>
                  </p>
                  <p className="mt-0.5 text-[11px] text-gray-400 truncate">{b.email || '—'} · {isEs ? 'agente' : 'agent'}: {b.agent} · {b.channel}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold ${cls}`}>{label}</span>
              </div>
            </div>
          );
        })}
      </Card>
    </div>
  );
}


function WelcomeIntentsSection({ isEs }) {
  const [d, setD] = useState(null);
  const [page, setPage] = useState(0);
  const [deleting, setDeleting] = useState('');
  const load = useCallback(() => {
    authFetch('/os2/welcome-intents').then((r) => r.json()).then(setD).catch(() => setD({ items: [], total: 0, with_email: 0, converted: 0 }));
  }, []);
  useEffect(() => { load(); }, [load]);
  const remove = async (w) => {
    const who = w.name || w.email || (isEs ? 'visitante anónimo' : 'anonymous visitor');
    if (!window.confirm(isEs
      ? `¿Eliminar la entrada de ${who}? Quedará registrada en la auditoría y se notificará al Event Bus.`
      : `Delete the entry from ${who}? It will be recorded in the audit trail and announced on the Event Bus.`)) return;
    setDeleting(w.id);
    try {
      const r = await authFetch(`/os2/admin-data/welcome_intents/${w.id}?reason=admin_cleanup`, { method: 'DELETE' });
      if (r.ok) load();
    } finally { setDeleting(''); }
  };
  const PER = 10;
  const items = d?.items || [];
  const totalPages = Math.max(1, Math.ceil(items.length / PER));
  const pg = Math.min(page, totalPages - 1);
  const rows = items.slice(pg * PER, pg * PER + PER);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(isEs ? 'es' : 'en', { day: '2-digit', month: 'short' }) : '');
  const CAT_ICON = { reviews: '⭐', marketing: '🎯', delivery: '🛵', costs: '📊', staff: '👥', opening: '🚀', pricing: '💰', website: '✨', demo: '🎬', other: '💬' };
  return (
    <div id="welcome-intents" data-testid="growth-welcome-intents">
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
          <LiveDot /> 👋 {isEs ? 'Welcome Gate · lo que cuentan los visitantes' : 'Welcome Gate · what visitors tell us'}
          {d && <span className="normal-case tracking-normal text-gray-300">· {d.total} {isEs ? 'total' : 'total'} · {d.with_email} {isEs ? 'con email' : 'with email'} · {d.converted} {isEs ? 'llegaron a la demo' : 'reached demo'}</span>}
        </p>
        {items.length > PER && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 tabular-nums">
            <button onClick={() => setPage(Math.max(0, pg - 1))} disabled={pg === 0} data-testid="welcome-int-prev" className="h-6 px-2.5 rounded-full border border-gray-200 bg-white font-semibold text-gray-600 disabled:opacity-35">‹</button>
            {pg * PER + 1}–{Math.min((pg + 1) * PER, items.length)} / {items.length}
            <button onClick={() => setPage(Math.min(totalPages - 1, pg + 1))} disabled={pg >= totalPages - 1} data-testid="welcome-int-next" className="h-6 px-2.5 rounded-full border border-gray-200 bg-white font-semibold text-gray-600 disabled:opacity-35">›</button>
          </span>
        )}
      </div>
      <Card className="p-2">
        {!d ? <p className="p-4 text-[12.5px] text-gray-400">…</p> : !rows.length ? (
          <p className="p-4 text-[12.5px] text-gray-400">{isEs ? 'Aún no hay respuestas — cada visitante que hable con Rachel en la portada aparecerá aquí con su dolor categorizado.' : 'No answers yet — every visitor who talks to Rachel on the homepage will show up here with their pain categorized.'}</p>
        ) : rows.map((w) => (
          <div key={w.id} className="px-3 py-2.5 border-b border-gray-50 last:border-0 hover:bg-orange-50/40 rounded-xl transition-colors" data-testid={`welcome-int-${w.id}`}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 shrink-0 rounded-full bg-[#FFF7ED] border border-orange-200 text-[14px] flex items-center justify-center">{CAT_ICON[w.category] || '💬'}</span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-gray-900 truncate">
                  {w.name || (isEs ? 'Visitante anónimo' : 'Anonymous visitor')}
                  {w.restaurant && <span className="text-gray-600 font-normal"> · {w.restaurant}</span>}
                  {w.email && <span className="text-gray-400 font-normal"> · {w.email}</span>}
                  <span className="ml-1.5 text-[10px] font-semibold text-gray-400 tabular-nums">· {fmt(w.created_at)}</span>
                </p>
                {w.message && <p className="mt-0.5 text-[11.5px] italic text-gray-500 truncate">“{w.message}”</p>}
                <p className="mt-1 flex flex-wrap gap-1">
                  <span className="rounded bg-orange-50 text-[#d8651a] px-1.5 py-px text-[9px] font-bold uppercase">{w.category}</span>
                  {w.role && <span className="rounded bg-gray-100 text-gray-500 px-1.5 py-px text-[9px] font-semibold uppercase">{w.role}</span>}
                  <span className="rounded bg-gray-100 text-gray-500 px-1.5 py-px text-[9px] font-semibold uppercase">{w.locale}</span>
                  {w.market && <span className="rounded bg-indigo-50 text-indigo-500 px-1.5 py-px text-[9px] font-bold uppercase">{w.market}</span>}
                  {w.ref_code && <span className="rounded bg-orange-50 text-[#d8651a] px-1.5 py-px text-[9px] font-bold uppercase">via {w.ref_code}</span>}
                  {w.nudge && (w.nudge.actions || []).includes('nudge_email') && <span className="rounded bg-blue-50 text-blue-500 px-1.5 py-px text-[9px] font-bold uppercase">↩️ {isEs ? 'nudge enviado' : 'nudge sent'}</span>}
                </p>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold ${w.reached_demo ? 'bg-emerald-50 text-emerald-600' : w.email ? 'bg-amber-50 text-amber-600' : 'bg-gray-100 text-gray-400'}`}>
                {w.reached_demo ? `🎬 ${isEs ? 'Llegó a la demo' : 'Reached demo'}` : w.email ? (isEs ? '✉️ Con email' : '✉️ Has email') : (isEs ? '👤 Anónimo' : '👤 Anonymous')}
              </span>
              <button onClick={() => remove(w)} disabled={deleting === w.id}
                title={isEs ? 'Eliminar (con auditoría y notificación)' : 'Delete (audited and announced)'}
                data-testid={`welcome-int-delete-${w.id}`}
                className="shrink-0 p-1.5 rounded-lg text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-40">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}


function DemoRequestsSection({ isEs }) {
  const [d, setD] = useState(null);
  const [page, setPage] = useState(0);
  useEffect(() => {
    authFetch('/os2/demo-requests').then((r) => r.json()).then(setD).catch(() => setD({ items: [], total: 0, verified: 0 }));
  }, []);
  const PER = 10;
  const items = d?.items || [];
  const totalPages = Math.max(1, Math.ceil(items.length / PER));
  const pg = Math.min(page, totalPages - 1);
  const rows = items.slice(pg * PER, pg * PER + PER);
  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString(isEs ? 'es' : 'en', { day: '2-digit', month: 'short' }) : '');
  return (
    <div id="demo-requests" data-testid="growth-demo-requests">
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
          <LiveDot /> 🎟️ {isEs ? 'Solicitudes de demo' : 'Demo requests'}
          {d && <span className="normal-case tracking-normal text-gray-300">· {d.total} {isEs ? 'total' : 'total'} · {d.verified} {isEs ? 'verificadas' : 'verified'}</span>}
        </p>
        {items.length > PER && (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400 tabular-nums">
            <button onClick={() => setPage(Math.max(0, pg - 1))} disabled={pg === 0} data-testid="demo-req-prev" className="h-6 px-2.5 rounded-full border border-gray-200 bg-white font-semibold text-gray-600 disabled:opacity-35">‹</button>
            {pg * PER + 1}–{Math.min((pg + 1) * PER, items.length)} / {items.length}
            <button onClick={() => setPage(Math.min(totalPages - 1, pg + 1))} disabled={pg >= totalPages - 1} data-testid="demo-req-next" className="h-6 px-2.5 rounded-full border border-gray-200 bg-white font-semibold text-gray-600 disabled:opacity-35">›</button>
          </span>
        )}
      </div>
      <Card className="p-2">
        {!d ? <p className="p-4 text-[12.5px] text-gray-400">…</p> : !rows.length ? (
          <p className="p-4 text-[12.5px] text-gray-400">{isEs ? 'Aún no hay solicitudes — cada visitante que pida la demo aparecerá aquí con su POS, objetivos y estado.' : 'No requests yet — every visitor who asks for the demo will show up here with their POS, goals and status.'}</p>
        ) : rows.map((r) => (
          <div key={r.id} className="px-3 py-2.5 border-b border-gray-50 last:border-0 hover:bg-orange-50/40 rounded-xl transition-colors" data-testid={`demo-req-${r.email}`}>
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 shrink-0 rounded-full bg-[#FFF7ED] border border-orange-200 text-[#F77F11] text-[12px] font-bold flex items-center justify-center uppercase">{(r.name || '?').slice(0, 1)}</span>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold text-gray-900 truncate">
                  {r.restaurant_name} <span className="text-gray-400 font-normal">· {r.name} ({r.role})</span>
                  <span className="ml-1.5 text-[10px] font-semibold text-gray-400 tabular-nums">· {fmt(r.created_at)}</span>
                </p>
                <p className="text-[11px] text-gray-400 truncate">{r.email}{r.phone ? ` · ${r.phone}` : ''} · {r.city}, {r.country}</p>
                <p className="mt-1 flex flex-wrap gap-1">
                  <span className="rounded bg-gray-900 text-white px-1.5 py-px text-[9px] font-bold uppercase">{r.pos}</span>
                  {r.visitor_path && <span className="rounded bg-violet-50 text-violet-600 px-1.5 py-px text-[9px] font-bold uppercase">{r.visitor_path === 'builder' ? '⚡ new era' : `🏛️ ${r.visitor_path}`}</span>}
                  {r.ai_time && <span className="rounded bg-violet-50 text-violet-600 px-1.5 py-px text-[9px] font-bold uppercase">🤖 AI: {r.ai_time}</span>}
                  {(r.delivery || []).slice(0, 3).map((x) => <span key={x} className="rounded bg-gray-100 text-gray-500 px-1.5 py-px text-[9px] font-semibold uppercase">{x}</span>)}
                  {(r.goals || []).slice(0, 2).map((g) => <span key={g} className="rounded bg-orange-50 text-[#d8651a] px-1.5 py-px text-[9px] font-bold uppercase">🎯 {g}</span>)}
                  {r.partner && <span className="rounded bg-orange-50 text-[#d8651a] px-1.5 py-px text-[9px] font-bold uppercase">via {r.partner.ref_code}{r.partner.name ? ` · ${r.partner.name}` : ''}</span>}
                  {r.comeback && <span className="rounded bg-blue-50 text-blue-500 px-1.5 py-px text-[9px] font-bold uppercase">↩️ {isEs ? 'recordatorio enviado' : 'nudge sent'}</span>}
                </p>
                {r.message && <p className="mt-1 text-[11px] italic text-gray-500 truncate">“{r.message}”</p>}
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold ${r.verified ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`} data-testid={`demo-req-status-${r.email}`}>
                {r.verified ? `✓ ${isEs ? 'Verificado' : 'Verified'}${r.visits ? ` · ${r.visits} ${isEs ? 'visitas' : 'visits'}` : ''}` : (isEs ? '📬 Pendiente email' : '📬 Email pending')}
              </span>
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}

function FollowupsSection({ isEs }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    authFetch('/os2/scan-followups').then((r) => r.json()).then(setD).catch(() => setD({ items: [], total: 0 }));
  }, []);
  const ago = (iso) => {
    if (!iso) return '';
    const days = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
    return days === 0 ? (isEs ? 'hoy' : 'today') : isEs ? `hace ${days} d` : `${days}d ago`;
  };
  const ACTION_LABEL = {
    lead_email: ['☎️', isEs ? 'Email al prospecto' : 'Prospect emailed'],
    partner_email: ['🤝', isEs ? 'Partner avisado' : 'Partner alerted'],
    admin_digest: ['📋', isEs ? 'En tu lista de llamadas' : 'On your call list'],
  };
  return (
    <div id="scan-followups" data-testid="growth-followups">
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
          <LiveDot /> ☎️ {isEs ? 'Seguimientos post-scan (3 días)' : 'Post-scan follow-ups (3 days)'}
          {d && <span className="normal-case tracking-normal text-gray-300">· {d.total} {isEs ? 'enviados' : 'sent'}</span>}
        </p>
      </div>
      <Card className="p-2">
        {!d ? <p className="p-4 text-[12.5px] text-gray-400">…</p> : !d.items.length ? (
          <p className="p-4 text-[12.5px] text-gray-400">
            {isEs ? 'Aún no hay seguimientos — Rachel contacta automáticamente a cada scan cuando cumple 3 días.' : 'No follow-ups yet — Rachel automatically reaches out to every scan when it turns 3 days old.'}
          </p>
        ) : d.items.map((f) => (
          <div key={f.scan_id} className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-50 last:border-0 hover:bg-orange-50/40 rounded-xl transition-colors" data-testid={`growth-followup-${f.scan_id}`}>
            <span className={`w-11 shrink-0 text-center rounded-lg py-1 text-[13px] font-black tabular-nums ${f.score >= 65 ? 'text-emerald-600 bg-emerald-50' : f.score >= 40 ? 'text-[#d8651a] bg-orange-50' : 'text-red-500 bg-red-50'}`}>{f.score ?? '—'}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-gray-900 truncate">
                {f.domain}
                {f.partner && (
                  <span className="ml-1.5 rounded bg-orange-50 text-[#d8651a] px-1.5 py-0.5 text-[9.5px] font-bold uppercase align-middle" title={f.partner.name} data-testid={`followup-partner-${f.scan_id}`}>
                    via {f.partner.ref_code}{f.partner.name ? ` · ${f.partner.name}` : ''}
                  </span>
                )}
              </p>
              <p className="text-[11px] text-gray-400 truncate">
                {isEs ? 'Recordatorio' : 'Reminder'} {ago(f.followup_at)} · {(f.actions.length ? f.actions : ['admin_digest']).map((a) => (ACTION_LABEL[a] || ['·', a]).join(' ')).join(' · ')}
              </p>
            </div>
            <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10.5px] font-bold ${f.responded ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-400'}`} data-testid={`followup-status-${f.scan_id}`}>
              {f.responded ? `✓ ${isEs ? 'Respondió' : 'Responded'}${f.lead_name ? ` · ${f.lead_name}` : ''}` : (isEs ? 'Esperando' : 'Waiting')}
            </span>
          </div>
        ))}
      </Card>
    </div>
  );
}

function ScansSection({ isEs, scans, page, setPage, onDelete }) {
  const scoreTone = (v) => (v >= 65 ? 'text-emerald-600 bg-emerald-50' : v >= 40 ? 'text-[#d8651a] bg-orange-50' : 'text-red-500 bg-red-50');
  return (
    <div id="live-scans" data-testid="growth-scans-list">
      <div className="mb-2 flex items-center justify-between flex-wrap gap-2">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
          <LiveDot /> <Radar className="w-3.5 h-3.5 text-[#F77F11]" /> {isEs ? 'Últimos scans del website' : 'Latest website scans'}
          {scans && <span className="normal-case tracking-normal text-gray-300">· {scans.total} {isEs ? 'en total' : 'total'}</span>}
        </p>
        {scans && scans.pages > 1 && <span className="text-[11px] text-gray-400">{isEs ? 'Página' : 'Page'} {scans.page}/{scans.pages}</span>}
      </div>
      <Card className="p-2">
        {!scans ? <p className="p-4 text-[12.5px] text-gray-400">…</p> : !scans.scans.length ? (
          <p className="p-4 text-[12.5px] text-gray-400">{isEs ? 'Aún no hay scans — comparte el Free Audit para generar los primeros.' : 'No scans yet — share the Free Audit to generate the first ones.'}</p>
        ) : scans.scans.map((s) => (
          <div key={s.scan_id} className="flex items-center gap-3 px-3 py-2.5 border-b border-gray-50 last:border-0 hover:bg-orange-50/40 rounded-xl transition-colors" data-testid={`growth-scan-${s.scan_id}`}>
            <span className={`w-11 shrink-0 text-center rounded-lg py-1 text-[13px] font-black tabular-nums ${scoreTone(s.score)}`}>{s.score}</span>
            <div className="min-w-0 flex-1">
              <p className="text-[13px] font-semibold text-gray-900 truncate">
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="hover:text-[#d8651a]">{s.domain}</a>
                {s.compare && <span className="ml-1.5 rounded bg-violet-50 text-violet-600 px-1.5 text-[9.5px] font-bold uppercase align-middle">VS</span>}
                {s.kind === 'partner_referred'
                  ? <span className="ml-1.5 rounded bg-sky-50 text-sky-600 px-1.5 py-0.5 text-[9.5px] font-bold uppercase align-middle">🤝 {isEs ? 'Vía partner' : 'Via partner'}{s.partner_name ? ` · ${s.partner_name}` : ''}</span>
                  : s.lead
                    ? <span className="ml-1.5 rounded bg-emerald-50 text-emerald-600 px-1.5 py-0.5 text-[9.5px] font-bold uppercase align-middle">⭐ New lead</span>
                    : <span className="ml-1.5 rounded bg-gray-100 text-gray-500 px-1.5 py-0.5 text-[9.5px] font-bold uppercase align-middle">{isEs ? 'Sin email' : 'No email'}</span>}
              </p>
              <p className="text-[11px] text-gray-400 truncate flex items-center gap-1">
                {s.lead
                  ? <><Unlock className="w-3 h-3 text-emerald-500" /> {s.lead.restaurant_name} · {s.lead.email}{s.lead.phone ? ` · ${s.lead.phone}` : ''}</>
                  : <><Lock className="w-3 h-3" /> {isEs ? 'No dejó su email — no desbloqueó el informe' : 'Left no email — did not unlock the report'}</>}
                {s.ref_code && <span className="text-[#d8651a] font-semibold"> · ref {s.ref_code}</span>}
              </p>
            </div>
            <span className="shrink-0 text-[11px] text-gray-400 tabular-nums text-right leading-tight">{daysAgoLabel(s.at, isEs)}</span>
            <button onClick={() => onDelete(s.scan_id)} title={isEs ? 'Eliminar scan/lead' : 'Delete scan/lead'}
              className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`growth-scan-delete-${s.scan_id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </Card>
      {scans && <Pager page={page} pages={scans.pages} setPage={setPage} testid="growth-scans-pager" />}
    </div>
  );
}

function SourceChip({ label, state, isEs }) {
  const ok = state === 'operational';
  const map = {
    operational: isEs ? 'operativo' : 'operational',
    waiting_google: isEs ? 'esperando a Google' : 'waiting for Google',
    setup_required: isEs ? 'configurar' : 'setup required',
    no_property: isEs ? 'sin propiedad' : 'no property found',
    permission_required: isEs ? 'permiso requerido' : 'permission required',
    restaurant_level: isEs ? 'por restaurante' : 'per restaurant',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10.5px] font-bold ${ok ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${ok ? 'bg-emerald-500' : 'bg-gray-400'}`} />
      {label} · {map[state] || (isEs ? 'no disponible' : 'unavailable')}
    </span>
  );
}

const CONV_METRICS = [
  { key: 'schedule_demo', en: 'Demo requests', es: 'Demos solicitadas', qEn: 'Are we creating more opportunities than last week?', qEs: '¿Generamos más oportunidades que la semana pasada?' },
  { key: 'newsletter_signup', en: 'Newsletter signups', es: 'Altas newsletter', qEn: 'Is the audience compounding over time?', qEs: '¿La audiencia crece de forma compuesta?' },
  { key: 'book_strategy_call', en: 'Strategy calls', es: 'Llamadas estratégicas', qEn: 'Are conversations becoming meetings?', qEs: '¿Las conversaciones se vuelven reuniones?' },
  { key: 'contact_form_submitted', en: 'Contact forms', es: 'Formularios contacto', qEn: 'Is interest growing or declining?', qEs: '¿El interés crece o cae?' },
  { key: 'contact_whatsapp', en: 'WhatsApp clicks', es: 'Clicks WhatsApp', qEn: 'Do visitors reach out instantly?', qEs: '¿Los visitantes contactan al instante?' },
  { key: 'contact_phone', en: 'Phone clicks', es: 'Clicks teléfono', qEn: 'Are we earning phone calls?', qEs: '¿Estamos ganando llamadas?' },
  { key: 'contact_email', en: 'Email clicks', es: 'Clicks email', qEn: 'Is email still a working channel?', qEs: '¿El email sigue funcionando?' },
  { key: 'affiliate_signup', en: 'Partner signups', es: 'Altas partner', qEn: 'Is the partner network expanding?', qEs: '¿La red de partners se expande?' },
  { key: 'pricing_cta_click', en: 'Pricing CTAs', es: 'CTAs de precios', qEn: 'Are visitors evaluating price seriously?', qEs: '¿Evalúan el precio en serio?' },
  { key: 'stripe_payment_completed', en: 'Payments', es: 'Pagos completados', qEn: 'Is intent becoming revenue?', qEs: '¿La intención se vuelve ingresos?' },
];

const CONV_TONES = {
  up: { main: '#10b981', soft: '#a7f3d0', txt: 'text-emerald-600', chip: 'bg-emerald-50 text-emerald-700' },
  flat: { main: '#f59e0b', soft: '#fde68a', txt: 'text-amber-600', chip: 'bg-amber-50 text-amber-700' },
  down: { main: '#ef4444', soft: '#fecaca', txt: 'text-red-500', chip: 'bg-red-50 text-red-600' },
  neutral: { main: '#94a3b8', soft: '#e2e8f0', txt: 'text-slate-400', chip: 'bg-slate-100 text-slate-500' },
};

function ConvCard({ m, conv, isEs }) {
  const weeks = conv.weeks || [];
  const weekly = weeks.map((w) => w.by_name?.[m.key] || 0);
  let run = 0;
  const cum = weekly.map((v) => { run += v; return run; });
  const total = run;
  const tw = conv.this_week?.[m.key] || 0;
  const lw = conv.last_week?.[m.key] || 0;
  const maxW = Math.max(...weekly, 0);
  const newHigh = tw > 0 && tw === maxW && weekly.filter((v) => v === tw).length === 1;
  const unusualDrop = lw >= 3 && tw <= lw * 0.5;
  const tone = total === 0 ? 'neutral' : (tw > lw || newHigh) ? 'up' : tw < lw ? 'down' : 'flat';
  const T = CONV_TONES[tone];
  const answer = tone === 'up' ? (isEs ? 'Sí — acelerando' : 'Yes — accelerating')
    : tone === 'down' ? (isEs ? 'Necesita atención' : 'Needs attention')
      : tone === 'flat' ? (isEs ? 'Estable' : 'Flat')
        : (isEs ? 'Sin actividad aún' : 'No activity yet');
  const maxCum = Math.max(...cum, 1);
  return (
    <Card data-testid={`growth-conv-${m.key}`} className="relative overflow-visible p-4 flex flex-col">
      <div className="flex items-start justify-between gap-1">
        <p className="text-[10.5px] uppercase tracking-wide font-bold text-gray-400">{isEs ? m.es : m.en}</p>
        {newHigh && <span className="relative flex h-2 w-2 mt-0.5" title={isEs ? 'Nuevo máximo' : 'New high'} data-testid={`growth-conv-${m.key}-newhigh`}>
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
        </span>}
        {unusualDrop && <Zap className="w-3 h-3 text-red-500 mt-0.5" title={isEs ? 'Caída inusual' : 'Unusual drop'} data-testid={`growth-conv-${m.key}-drop`} />}
      </div>
      <p className="mt-0.5 text-[10px] text-gray-400 leading-tight italic">{isEs ? m.qEs : m.qEn}</p>
      <div className="mt-2 flex items-baseline gap-2 flex-wrap">
        <span className={`text-[22px] font-semibold ${total === 0 ? 'text-gray-300' : 'text-gray-900'}`}>{tw}</span>
        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${T.chip}`}>{answer}</span>
      </div>
      <p className={`mt-0.5 text-[10px] font-semibold ${T.txt}`}>
        {tw >= lw ? '▲' : '▼'} {tw - lw >= 0 ? '+' : ''}{tw - lw} {isEs ? 'vs sem. ant.' : 'vs last wk'} · Σ 8w: {total}
      </p>
      <div className="mt-3 flex items-end gap-[3px] h-10 rounded-lg bg-gray-50/80 px-1.5 pt-1.5 pb-0 border-b-2 border-gray-100">
        {cum.map((cv, i) => (
          <div key={i} className="relative flex-1 group/bar h-full flex items-end">
            <div className="w-full rounded-t-sm transition-all duration-200 group-hover/bar:brightness-90"
              style={{ height: total === 0 ? '2px' : `${Math.max(6, (cv / maxCum) * 100)}%`, background: i === cum.length - 1 ? T.main : T.soft }} />
            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/bar:block z-20 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-[10px] text-white shadow-lg">
              {weeks[i]?.start}: +{weekly[i]} · {isEs ? 'acum.' : 'cum.'} {cv}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function SectionSkeleton({ rows = 1, h = 'h-24', testid }) {
  return (
    <div className="animate-pulse" data-testid={testid}>
      <div className="mb-2 h-3 w-44 rounded bg-gray-100" />
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-3`}>
        {Array.from({ length: rows * 4 }).map((_, i) => <div key={i} className={`rounded-2xl bg-gray-100/80 ${h}`} />)}
      </div>
    </div>
  );
}

export default function GrowthCenterPage() {
  const { lang } = useT();
  const isEs = lang === 'es';
  const [d, setD] = useState(null);
  const [conv, setConv] = useState(null);
  const [scans, setScans] = useState(null);
  const [scanPage, setScanPage] = useState(1);
  const { hash } = useLocation();
  const load = useCallback((refresh = false) => {
    if (refresh) setD(null);
    authFetch(`/admin/google-suite/intelligence${refresh ? '?refresh=true' : ''}`)
      .then((r) => r.json()).then(setD).catch(() => setD({ status: 'error' }));
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    authFetch('/engagement/conversions').then((r) => (r.ok ? r.json() : null)).then(setConv).catch(() => {});
  }, []);
  useEffect(() => {
    authFetch(`/os2/scans?page=${scanPage}`).then((r) => r.json()).then(setScans).catch(() => {});
  }, [scanPage]);
  useEffect(() => {
    if (hash === '#scans' && scans) setTimeout(() => document.getElementById('live-scans')?.scrollIntoView({ behavior: 'smooth' }), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, !!scans]);
  const delScan = async (id) => {
    if (!window.confirm(isEs ? '¿Eliminar este scan y su lead asociado? Esta acción no se puede deshacer.' : 'Delete this scan and its associated lead? This cannot be undone.')) return;
    await authFetch(`/os2/scans/${id}`, { method: 'DELETE' });
    authFetch(`/os2/scans?page=${scanPage}`).then((r) => r.json()).then(setScans).catch(() => {});
  };

  const { ga4, gsc, ads, gbp } = d || {};
  const googleReady = d && !(d.status === 'setup_required' && !d.is_demo);

  return (
    <div data-testid="growth-center" className="space-y-6">
      {/* Header — stable from first paint, never replaced */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-tight text-gray-900">Growth Center</h1>
          <p className="mt-1 text-[12.5px] text-gray-500">
            {isEs ? 'Tu COO de IA ya revisó el negocio — esto es lo que importa hoy' : 'Your AI COO already reviewed the business — here is what matters today'}
            {d?.fetched_at && <> · {isEs ? 'actualizado' : 'updated'} {ago(d.fetched_at, isEs)}</>}
            <button onClick={() => load(true)} className="ml-2 align-middle text-gray-400 hover:text-gray-600" data-testid="growth-refresh">
              <RefreshCw className="w-3 h-3" />
            </button>
          </p>
          <div className="mt-1"><RangeNotice isEs={isEs} windows={isEs ? 'tiempo real y bloques de Google API (28 días). Business Pulse sí sigue el selector' : 'realtime and Google API blocks (28 days). Business Pulse DOES follow the selector'} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {d?.is_demo && (
            <span className="rounded-full bg-gray-900 text-white px-3 py-1 text-[10.5px] font-bold tracking-wide" data-testid="growth-demo-badge">
              {isEs ? 'MODO DEMO — DATOS DE EJEMPLO' : 'DEMO MODE — SAMPLE DATA'}
            </span>
          )}
          {d?.sources?.gsc && <SourceChip label="Search" state={d.sources.gsc.state} isEs={isEs} />}
          {d?.sources?.ga4 && <SourceChip label="GA4" state={d.sources.ga4.state} isEs={isEs} />}
          {d?.sources?.ads && <SourceChip label="Ads" state={d.sources.ads.state} isEs={isEs} />}
          {d?.sources?.gbp && <SourceChip label="Business" state={d.sources.gbp.state} isEs={isEs} />}
        </div>
      </div>

      {d?.is_demo && (
        <p className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-2.5 text-[12px] text-gray-600">
          {isEs
            ? 'Estás viendo un entorno de demo realista de KITXENS. Estas cifras son simuladas y no representan un negocio real.'
            : 'You are viewing a realistic KITXENS demo environment. These figures are simulated and do not represent a live business.'}
        </p>
      )}

      {/* 1 · Business Pulse — the hero */}
      <BusinessPulse isEs={isEs} />

      {/* 2 · Rachel — the COO reads the business to you */}
      <RachelExecutiveBrief isEs={isEs} />

      {/* 3 · Opportunity, risk, scans, AI team */}
      {!d ? (
        <SectionSkeleton testid="growth-skel-narrative" />
      ) : d.status === 'setup_required' && !d.is_demo ? (
        <>
          <Card className="p-8 text-center">
            <p className="text-[14px] font-semibold text-gray-900">{isEs ? 'Conecta Google para activar la inteligencia de crecimiento' : 'Connect Google to activate growth intelligence'}</p>
            <p className="mt-1 text-[12.5px] text-gray-500">{isEs ? 'Search Console y Google Analytics se conectan desde el Hub en un clic.' : 'Search Console and Google Analytics connect from the Hub in one click.'}</p>
            <Link to="/hub" className="mt-4 inline-flex rounded-full bg-[#F77F11] px-5 py-2 text-[12.5px] font-bold text-white">{isEs ? 'Ir al Hub → Conectar' : 'Go to Hub → Connect'}</Link>
          </Card>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <ScansPulse isEs={isEs} scans={scans} />
            <AiTeamCard d={d} scans={scans} isEs={isEs} />
          </div>
        </>
      ) : d.narrative && Object.keys(d.narrative).length > 0 ? (
        <Fade>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-3" data-testid="growth-narrative">
            {NARR.map(({ key, icon: Icon, en, es, tone }) => (
              <React.Fragment key={key}>
                {d.narrative[key] && (
                  <Card className="p-4">
                    <p className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${tone}`}>
                      <Icon className="w-3 h-3" /> {isEs ? es : en}
                    </p>
                    <p className="mt-2 text-[12.5px] leading-relaxed text-gray-700">{isEs ? d.narrative[key].es : d.narrative[key].en}</p>
                  </Card>
                )}
                {key === 'opportunity' && (
                  <>
                    <ScansPulse isEs={isEs} scans={scans} />
                    <AiTeamCard d={d} scans={scans} isEs={isEs} />
                  </>
                )}
              </React.Fragment>
            ))}
          </div>
        </Fade>
      ) : (
        <Fade>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <ScansPulse isEs={isEs} scans={scans} />
            <AiTeamCard d={d} scans={scans} isEs={isEs} />
          </div>
        </Fade>
      )}

      {/* 4 · Traffic & search truth — GA4, GSC (skeletons while Google answers) */}
      {!d && <SectionSkeleton testid="growth-skel-ga4" />}
      {googleReady && ga4 && (
        <div data-testid="growth-ga4">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
            <BarChart3 className="w-3.5 h-3.5 text-[#F9AB00]" /> Google Analytics · {ga4.property_name || ga4.property}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label={isEs ? 'Usuarios activos' : 'Active users'} value={fmt(ga4.totals.active_users)} delta={ga4.delta.active_users} />
            <Kpi label={isEs ? 'Sesiones' : 'Sessions'} value={fmt(ga4.totals.sessions)} delta={ga4.delta.sessions} />
            <Kpi label={isEs ? 'Tasa engagement' : 'Engagement rate'} value={ga4.totals.engagement_rate} suffix="%" />
            <Kpi label={isEs ? 'Eventos clave' : 'Key events'} value={fmt(ga4.totals.key_events)} delta={ga4.delta.key_events} />
          </div>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-3 gap-3">
            <Card className="p-4 lg:col-span-2">
              <p className="text-[11px] font-bold text-gray-500 mb-2">{isEs ? 'Usuarios y sesiones · diario' : 'Users & sessions · daily'}</p>
              <LineChart data={ga4.trend} k1="users" k2="sessions" isEs={isEs}
                l1={isEs ? 'Usuarios' : 'Users'} l2={isEs ? 'Sesiones' : 'Sessions'} />
            </Card>
            <Card className="p-4">
              <p className="text-[11px] font-bold text-gray-500 mb-2">{isEs ? 'Canales' : 'Channels'}</p>
              {(ga4.channels || []).slice(0, 6).map((c) => {
                const max = ga4.channels[0]?.sessions || 1;
                return (
                  <div key={c.channel} className="mb-2">
                    <div className="flex justify-between text-[11.5px]"><span className="text-gray-600">{c.channel}</span><b className="tabular-nums text-gray-900">{fmt(c.sessions)}</b></div>
                    <div className="mt-0.5 h-1.5 rounded-full bg-gray-100"><div className="h-1.5 rounded-full bg-[#F9AB00]" style={{ width: `${(c.sessions / max) * 100}%` }} /></div>
                  </div>
                );
              })}
            </Card>
          </div>
        </div>
      )}

      {googleReady && gsc && (
        <div data-testid="growth-gsc">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
            <Search className="w-3.5 h-3.5 text-[#4285F4]" /> Search Console · {gsc.site}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Clicks" value={fmt(gsc.totals.clicks)} delta={gsc.delta.clicks} />
            <Kpi label={isEs ? 'Impresiones' : 'Impressions'} value={fmt(gsc.totals.impressions)} delta={gsc.delta.impressions} />
            <Kpi label="CTR" value={gsc.totals.ctr} suffix="%" />
            <Kpi label={isEs ? 'Posición media' : 'Avg position'} value={gsc.totals.position} />
          </div>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <Card className="p-4">
              <p className="text-[11px] font-bold text-gray-500 mb-2">{isEs ? 'Top búsquedas' : 'Top queries'}</p>
              {(gsc.top_queries || []).slice(0, 8).map((q) => (
                <div key={q.query} className="flex items-center justify-between py-1 text-[12px] border-b border-gray-50 last:border-0">
                  <span className="truncate mr-2 text-gray-600">{q.query}</span>
                  <span className="shrink-0 flex items-center gap-2">
                    <span className="rounded bg-gray-100 px-1.5 text-[10px] font-bold text-gray-500">#{q.position}</span>
                    <b className="tabular-nums text-gray-900">{fmt(q.clicks)}</b>
                    {q.delta_clicks != null && <span className={`text-[10px] font-bold ${q.delta_clicks >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{q.delta_clicks >= 0 ? '+' : ''}{q.delta_clicks}</span>}
                  </span>
                </div>
              ))}
            </Card>
            <div className="space-y-3">
              {(gsc.low_ctr_opps || []).slice(0, 3).map((o) => (
                <Card key={o.query} className="p-3.5 border-l-4" style={{ borderLeftColor: '#F77F11' }}>
                  <p className="text-[10px] uppercase tracking-wide font-bold text-[#d8651a]">{isEs ? 'Oportunidad' : 'Opportunity'} · ~{o.est_extra_clicks} clicks</p>
                  <p className="mt-1 text-[12px] text-gray-700">{isEs ? o.recommendation_es : o.recommendation_en}</p>
                </Card>
              ))}
              {(gsc.near_page_one || []).length > 0 && (
                <Card className="p-3.5">
                  <p className="text-[10px] uppercase tracking-wide font-bold text-sky-600">{isEs ? 'Cerca de página uno (pos. 4–10)' : 'Near page one (pos. 4–10)'}</p>
                  {gsc.near_page_one.slice(0, 4).map((n) => (
                    <div key={n.query} className="flex justify-between py-0.5 text-[11.5px]">
                      <span className="truncate mr-2 text-gray-600">{n.query}</span>
                      <span className="shrink-0 text-gray-500">#{n.position} · {fmt(n.impressions)} impr.</span>
                    </div>
                  ))}
                </Card>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 5 · First-party conversions — weekly trend */}
      {conv && (
        <div data-testid="growth-conversions">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
            <BarChart3 className="w-3.5 h-3.5 text-[#F77F11]" /> {isEs ? 'Conversiones · medición propia (8 semanas)' : 'Conversions · first-party (8 weeks)'}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {CONV_METRICS.map((m) => <ConvCard key={m.key} m={m} conv={conv} isEs={isEs} />)}
          </div>
        </div>
      )}

      {/* 6 · Growth recommendations */}
      {googleReady && d.recommendations?.length > 0 && (
        <div data-testid="growth-recommendations">
          <p className="mb-2 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">{isEs ? 'Recomendaciones para crecer' : 'Growth recommendations'}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {d.recommendations.map((r, i) => (
              <Card key={i} className="p-4 border-l-4" style={{ borderLeftColor: r.priority === 'high' ? '#ef4444' : '#F77F11' }} data-testid={`growth-rec-${i}`}>
                <p className={`text-[10px] uppercase tracking-wide font-bold ${r.priority === 'high' ? 'text-red-500' : 'text-[#d8651a]'}`}>
                  {r.priority === 'high' ? (isEs ? 'Prioridad alta' : 'High priority') : (isEs ? 'Sugerencia' : 'Suggestion')} · {r.source}
                </p>
                <p className="mt-1 text-[13px] font-semibold text-gray-900 leading-snug">{isEs ? r.title_es : r.title_en}</p>
                <p className="mt-0.5 text-[12px] text-gray-600 leading-relaxed">{isEs ? r.detail_es : r.detail_en}</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* 7 · Google Ads */}
      {googleReady && (
        <div data-testid="growth-ads">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
            <Megaphone className="w-3.5 h-3.5 text-[#34A853]" /> Google Ads
          </p>
          {ads ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Kpi label={isEs ? 'Inversión' : 'Spend'} value={`$${fmt(ads.spend)}`} />
                <Kpi label="Clicks" value={fmt(ads.clicks)} />
                <Kpi label="CTR" value={ads.ctr} suffix="%" />
                <Kpi label={isEs ? 'Conversiones' : 'Conversions'} value={fmt(ads.conversions)} />
                <Kpi label="ROAS" value={`${ads.roas}x`} />
              </div>
              <Card className="mt-3 p-4">
                <p className="text-[11px] font-bold text-gray-500 mb-2">{isEs ? 'Campañas' : 'Campaigns'}</p>
                <table className="w-full text-[12px]">
                  <thead><tr className="text-left text-[10px] uppercase text-gray-400">
                    <th className="pb-1">{isEs ? 'Campaña' : 'Campaign'}</th><th className="pb-1 text-right">{isEs ? 'Inversión' : 'Spend'}</th>
                    <th className="pb-1 text-right">Clicks</th><th className="pb-1 text-right">Conv.</th><th className="pb-1 text-right">ROAS</th></tr></thead>
                  <tbody>
                    {ads.campaigns.map((c) => (
                      <tr key={c.name} className="border-t border-gray-50">
                        <td className="py-1.5 text-gray-700">{c.name}</td>
                        <td className="py-1.5 text-right tabular-nums">${fmt(c.spend)}</td>
                        <td className="py-1.5 text-right tabular-nums">{fmt(c.clicks)}</td>
                        <td className="py-1.5 text-right tabular-nums">{c.conversions}</td>
                        <td className={`py-1.5 text-right tabular-nums font-bold ${c.roas >= 3 ? 'text-emerald-600' : c.roas >= 1.5 ? 'text-gray-900' : 'text-red-500'}`}>{c.roas}x</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {ads.wasted_note && <p className="mt-2 rounded-lg bg-red-50 px-2.5 py-1.5 text-[11.5px] text-red-600">⚠ {isEs ? ads.wasted_note.es : ads.wasted_note.en}</p>}
              </Card>
            </>
          ) : (
            <Card className="p-4">
              <p className="text-[12.5px] text-gray-600">
                {d.sources?.ads?.pending_token
                  ? (isEs ? 'Google Ads conectado. El acceso a reportes está pendiente de la aprobación del developer token de Google.' : 'Google Ads connected. Reporting access pending Google developer-token approval.')
                  : (isEs ? 'Conecta Google Ads desde el Hub para ver inversión, campañas y ROAS aquí.' : 'Connect Google Ads from the Hub to see spend, campaigns and ROAS here.')}
              </p>
            </Card>
          )}
        </div>
      )}

      {/* 8 · Business Profile */}
      {googleReady && (
        <div data-testid="growth-gbp">
          <p className="mb-2 flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">
            <Store className="w-3.5 h-3.5 text-[#EA4335]" /> Business Profile
          </p>
          {gbp?.locations?.length ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {gbp.locations.map((l) => (
                <Card key={l.name} className={`p-4 ${l.name === gbp.best ? 'ring-1 ring-emerald-200' : ''}`}>
                  <p className="flex items-center gap-1.5 text-[12.5px] font-bold text-gray-900"><MapPin className="w-3.5 h-3.5 text-[#EA4335]" />{l.name}
                    {l.name === gbp.best && <span className="rounded-full bg-emerald-50 px-1.5 text-[9.5px] font-bold text-emerald-600">TOP</span>}
                    {l.name === gbp.worst && <span className="rounded-full bg-amber-50 px-1.5 text-[9.5px] font-bold text-amber-600">{isEs ? 'ATENCIÓN' : 'WATCH'}</span>}
                  </p>
                  <div className="mt-2 grid grid-cols-2 gap-y-1.5 text-[11.5px] text-gray-600">
                    <span>{isEs ? 'Búsqueda' : 'Search'}: <b className="text-gray-900">{fmt(l.search_impr)}</b></span>
                    <span>Maps: <b className="text-gray-900">{fmt(l.maps_impr)}</b></span>
                    <span>{isEs ? 'Llamadas' : 'Calls'}: <b className="text-gray-900">{fmt(l.calls)}</b></span>
                    <span>{isEs ? 'Rutas' : 'Directions'}: <b className="text-gray-900">{fmt(l.directions)}</b></span>
                  </div>
                  {l.missing?.length > 0 && (
                    <p className="mt-2 text-[10.5px] text-amber-600">⚠ {isEs ? 'Perfil incompleto' : 'Incomplete profile'}: {l.missing.join(', ')}</p>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-4">
              <p className="text-[12.5px] text-gray-600">{isEs ? d.sources?.gbp?.message_es : d.sources?.gbp?.message_en}</p>
              <Link to="/restaurant/connections" className="mt-1 inline-block text-[11.5px] font-semibold text-[#d8651a] underline underline-offset-2">
                {isEs ? 'Abrir conexiones del restaurante →' : 'Open restaurant connections →'}
              </Link>
            </Card>
          )}
        </div>
      )}

      {/* 9 · Pipeline evidence — scans, agent bridge, intents, requests, follow-ups */}
      <ScansSection isEs={isEs} scans={scans} page={scanPage} setPage={setScanPage} onDelete={delScan} />
      <AgentBridgeSection isEs={isEs} />
      <WelcomeIntentsSection isEs={isEs} />
      <DemoRequestsSection isEs={isEs} />
      <FollowupsSection isEs={isEs} />
      <AdminKitxensEffect isEs={isEs} />

      {/* 10 · Proprietary scores — the consequence of everything above, not the introduction */}
      {googleReady && d.scores?.length > 0 && (
        <div data-testid="growth-scores">
          <p className="mb-2 text-[11px] uppercase tracking-[0.15em] font-bold text-gray-400">{isEs ? 'Scores propietarios KITXENS — la consecuencia de todo lo anterior' : 'KITXENS proprietary scores — the consequence of everything above'}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {d.scores.map((s) => <ScoreCard key={s.key} s={s} isEs={isEs} />)}
          </div>
        </div>
      )}

      <EstimateDisclaimer isEs={isEs} testid="admin-estimate-disclaimer" />
    </div>
  );
}
